import ActiveOptionViewState from '../store/schema/ActiveOptionViewState';
import MessageListViewState from '../store/schema/MessageListViewState';
import QuickActionsViewState from '../store/schema/QuickActionsViewState';
import SignatureViewState from '../store/schema/SignatureViewState';
import TimeZoneViewState from '../store/schema/TimeZoneViewState';
import UserConfigurationDictionaryObjectType from 'owa-service/lib/contract/UserConfigurationDictionaryObjectType';
import WeekStartViewState from '../store/schema/WeekStartViewState';
import {
    UpdateUserConfigurationEntry,
    UserOptionsUpdateConfigName,
} from 'owa-session-store/lib/types/UpdateUserConfigurationEntry';

/*
This type consists of the union of all possible keys possible in the OptionsViewState
*/
type OptionsViewStateKeyType = keyof (MessageListViewState &
    QuickActionsViewState &
    SignatureViewState &
    TimeZoneViewState &
    WeekStartViewState);

/*
Type where the map keys are optional and the union of the keys of all the viewStates and values are string type
Used for the dictionary where we suggest alternate Keys
*/
type AlternateKeyNamesMapType = { [k in OptionsViewStateKeyType]?: string };

// helper to get the type of any value in UserConfigurationDictionaryEntry's key type
// exported for testing purposes only
export const getDictionaryEntryTypeForValue = (
    value: any
): UserConfigurationDictionaryObjectType => {
    if (value === null || value === undefined) {
        // we have to consider cases where value is falsy yet valid like number 0 or an empty string
        return null;
    }
    switch (typeof value) {
        case 'number':
            return 'Integer32';
        case 'boolean':
            return 'Boolean';
        case 'string':
            return 'String';
        // we will handle the other cases later
    }
    return 'String'; // when none of the cases are touched it returns string - we could have added this in the switch case as default, but keeping it here for future proofing
};

/**
 * Method to get the list of UserConfigurationDictionaryEntry objects to be sent to session store for update in case of a non mailbox configuration option
 * This will be passed as an input to the updateUserConfiguration util from the session store which performs the service call in the backend as well.
 * @param activeViewState the activeViewState which contains the changes
 * @param changeKeys the list of keys whose changes need to be included
 * @param alternateKeyNames in case the keys have different names to be present in the returned dictionary
 */
function getUserConfigurationChangesDictionary<
    TConfig extends UserOptionsUpdateConfigName = 'OWA.UserOptions'
>(
    activeViewState: ActiveOptionViewState,
    changeKeys: OptionsViewStateKeyType[],
    alternateKeyNames?: AlternateKeyNamesMapType
): UpdateUserConfigurationEntry<TConfig>[] {
    return (
        activeViewState &&
        changeKeys &&
        !!changeKeys.length &&
        changeKeys
            .map(
                (changeKey: OptionsViewStateKeyType) =>
                    changeKey in activeViewState && {
                        // ensure that the key exists in the current view state
                        key: (changeKey in alternateKeyNames
                            ? alternateKeyNames[changeKey as any]
                            : changeKey) as any,
                        valuetype: getDictionaryEntryTypeForValue(activeViewState[changeKey]),
                        value: [`${activeViewState[changeKey]}`],
                    }
            )
            .filter(Boolean)
    ); // this is to ensure that falsy entries are removed from the array using the boolean constructor
}
// https://stackoverflow.com/questions/32906887/remove-all-falsy-values-from-an-array

export default getUserConfigurationChangesDictionary;

export { OptionsViewStateKeyType, AlternateKeyNamesMapType };
